import React, { useState } from 'react';
import { supabase, getCurrentUser } from '../lib/supabase';
import { useDemo } from '../lib/demoContext';
import { DEMO_METRICS, DEMO_PHQ4_ENTRIES, DEMO_PROFILE, DEMO_KEY_LABS } from '../lib/demoData';
import { buildAreteBundle } from '../lib/fhir/bundleBuilder';
import { validateBundle } from '../lib/fhir/validator';
import InfoTooltip from './InfoTooltip';

/**
 * Download button for FHIR R4 JSON export.
 * Fetches all patient data, builds a FHIR Bundle, and triggers a download.
 */
export default function FhirExportButton({ compact }: { compact?: boolean }) {
  const demo = useDemo();
  const [loading, setLoading] = useState(false);

  async function handleExport() {
    setLoading(true);
    try {
      let profile: { id: string; full_name?: string | null; dob?: string | null },
          dailySummaries: Array<{ date: string; hrv?: number | null; lowest_hr?: number | null; total_sleep_mins?: number | null; rem_sleep_mins?: number | null; light_sleep_mins?: number | null; steps?: number | null; exercise_mins?: number | null; bedtime_start?: string | null }>,
          labs: Array<{ marker: string; value: number; unit: string; ref_low: number | null; ref_high: number | null; date: string }>,
          phq4Entries: Array<{ id: string; q1: number; q2: number; q3: number; q4: number; score: number; created_at: string }>,
          phenotypes: Array<{ id: string; name: string; score: number; created_at?: string }>;

      if (demo) {
        profile = DEMO_PROFILE;
        dailySummaries = DEMO_METRICS;
        labs = DEMO_KEY_LABS.map(l => ({ marker: l.marker, value: l.value, unit: l.unit, ref_low: l.ref_low, ref_high: l.ref_high, date: l.date }));
        phq4Entries = DEMO_PHQ4_ENTRIES.map(e => ({ id: e.id, q1: e.q1, q2: e.q2, q3: e.q3, q4: e.q4, score: e.q1 + e.q2 + e.q3 + e.q4, created_at: e.created_at }));
        phenotypes = [];
      } else {
        const user = await getCurrentUser();
        if (!user) { setLoading(false); return; }

        const [profileRes, metricsRes, labsRes, phq4Res] = await Promise.all([
          supabase.from('profiles').select('id, full_name, dob').eq('id', user.id).maybeSingle(),
          supabase.from('daily_health_summary').select('date, hrv_rmssd, lowest_heart_rate, total_sleep_mins, rem_sleep_mins, light_sleep_mins, steps, high_activity_mins, bedtime_start').eq('user_id', user.id).order('date', { ascending: true }),
          supabase.from('labs').select('marker, value, unit, ref_low, ref_high, date').eq('user_id', user.id).order('date', { ascending: false }),
          supabase.from('phq4').select('id, q1, q2, q3, q4, score, created_at').eq('user_id', user.id).order('created_at', { ascending: false }),
        ]);

        profile = profileRes.data ?? { id: user.id, full_name: user.email };
        dailySummaries = (metricsRes.data ?? []).map(r => ({
          date: r.date, hrv: r.hrv_rmssd, lowest_hr: r.lowest_heart_rate,
          total_sleep_mins: r.total_sleep_mins, rem_sleep_mins: r.rem_sleep_mins,
          light_sleep_mins: r.light_sleep_mins, steps: r.steps,
          exercise_mins: r.high_activity_mins, bedtime_start: r.bedtime_start,
        }));
        labs = labsRes.data ?? [];
        phq4Entries = phq4Res.data ?? [];
        phenotypes = [];
      }

      const now = new Date();
      const endDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const startDate = dailySummaries.length > 0 ? dailySummaries[0].date : endDate;

      const bundle = buildAreteBundle({ profile, dailySummaries, labs, phq4Entries, phenotypes, startDate, endDate });

      // Validate before download
      const validation = validateBundle(bundle);
      if (!validation.valid) {
        console.warn('[FHIR] Validation errors:', validation.errors);
      }

      // Trigger download
      const json = JSON.stringify(bundle, null, 2);
      const blob = new Blob([json], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (profile.full_name || 'patient').replace(/[^a-zA-Z0-9]/g, '_');
      a.href = url;
      a.download = `arete-fhir-export-${safeName}-${endDate}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[FHIR] Export error:', e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={handleExport}
        disabled={loading}
        className={`flex items-center gap-2 text-left w-full rounded-xl px-3 py-2 transition-colors ${
          compact ? 'text-xs' : 'text-sm'
        } text-arete-muted dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50`}
      >
        <span className="text-sm">📋</span>
        <span>{loading ? 'Exporting…' : 'FHIR Export'}</span>
      </button>
      <InfoTooltip text="FHIR is the standard format for sharing health data with clinicians and EHR systems like Epic and Cerner." ariaLabel="About FHIR export" />
    </div>
  );
}
